import dayjs from 'dayjs';
import React from 'react';

import Text from '@/atoms/Text';
import { Chart, type ChartSeriesConfig } from '@/components/common/Chart';
import type { AllocationTimelineData, AllocationTimelineRow } from '@/models/teamCharts.model';

import { CATEGORICAL_PALETTE } from '../common/chartPalette';

const ROW_HEIGHT = 44;
const CHART_EXTRA_HEIGHT = 60;

export interface UserAllocationTimelineChartProps {
  data: AllocationTimelineData;
  height?: number;
}

export interface TimelineTooltipProps {
  active?: boolean;
  payload?: {
    payload?: AllocationTimelineRow;
    seriesId?: string;
    segmentData?: unknown;
  }[];
}

export function TimelineTooltip({
  active,
  payload,
}: Readonly<TimelineTooltipProps>): React.ReactElement | null {
  if (!active || !payload?.length) {
    return null;
  }

  const row = payload[0].payload;
  const segmentData = payload[0].segmentData;

  if (!row || !segmentData) {
    return null;
  }

  const segment = segmentData as {
    allocationStartDate: string;
    allocationEndDate: string;
    allocationPercent: number;
    allocationHours: number;
    reportHours: number;
  };

  return (
    <div className="team-charts__tooltip">
      <Text variant="bodyDefaultStrong" className="team-charts__tooltip-title">
        {row.name}
      </Text>

      <div className="team-charts__tooltip-row">
        <Text variant="bodySmall" className="team-charts__tooltip-row-label">
          {segment.allocationStartDate} → {segment.allocationEndDate}
        </Text>

        <Text variant="bodySmall" color="var(--text-secondary)">
          Allocation {segment.allocationPercent}% · {segment.allocationHours}h allocated ·{' '}
          {segment.reportHours}h reported
        </Text>
      </div>
    </div>
  );
}

export function UserAllocationTimelineChart({
  data,
  height = 280,
}: Readonly<UserAllocationTimelineChartProps>): React.ReactElement | null {
  const { rows, series, domain } = data;

  const hasData = rows.some((row) => row.segments.length > 0);
  const chartHeight = Math.max(height, rows.length * ROW_HEIGHT + CHART_EXTRA_HEIGHT);

  // Vertical "today" marker, drawn only when today falls inside the visible date range.
  const todayTs = dayjs().startOf('day').valueOf();
  const todayHoverContent = (
    <div className="team-charts__tooltip">
      <Text variant="bodyDefaultStrong" className="team-charts__tooltip-title">
        Today
      </Text>

      <div className="team-charts__tooltip-row">
        <Text variant="bodySmall" className="team-charts__tooltip-row-label">
          {dayjs(todayTs).format('YYYY-MM-DD')}
        </Text>
      </div>
    </div>
  );
  const todayReferenceLines =
    todayTs >= domain[0] && todayTs <= domain[1]
      ? [
          {
            axis: 'x' as const,
            value: todayTs,
            color: 'var(--state-error)',
            strokeWidth: 2,
            hoverContent: todayHoverContent,
            dashed: false,
          },
        ]
      : [];

  if (!hasData) {
    return (
      <Text variant="bodySmall" color="var(--text-secondary)">
        No allocation timeline data available.
      </Text>
    );
  }

  /*
   * IMPORTANT:
   *
   * Keep dataKey as a FUNCTION.
   *
   * The chart data lives at:
   *
   * row.values[s.dataKey]
   *
   * The id is used only to identify the allocation
   * for the tooltip.
   */
  const chartSeries: ChartSeriesConfig<AllocationTimelineRow>[] = series.map((s, idx) => ({
    id: s.dataKey,
    dataKey: (row) => row.values[s.dataKey] ?? null,
    name: s.name,
    color: CATEGORICAL_PALETTE[idx % CATEGORICAL_PALETTE.length],
  }));

  return (
    <div className="team-charts__chart">
      <Text variant="bodyDefaultStrong" className="team-charts__chart-title">
        User Allocation Timeline
      </Text>

      <div
        style={{
          overflowX: 'auto',
          overflowY: 'visible',
          width: '100%',
          minHeight: height,
          display: 'block',
        }}
      >
        <div style={{ minWidth: '100%', width: 'fit-content' }}>
          <div className="team-charts__timeline-scroll" style={{ maxHeight: height }}>
            <Chart<AllocationTimelineRow>
              type="rangeBar"
              data={rows}
              series={chartSeries}
              xAxis={{
                type: 'number',
                domain,
                tickFormatter: (value) => dayjs(value).format('MMM YYYY'),
              }}
              yAxis={{
                type: 'category',
                dataKey: 'name',
                width: 120,
              }}
              tooltip={TimelineTooltip}
              referenceLines={todayReferenceLines}
              barSize={10}
              height={chartHeight}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default UserAllocationTimelineChart;
